import React, { useCallback, useEffect, useMemo, useState } from "react";
import PaginatedSearchList from "../../../../components/PaginatedSearchList";
import Form from "react-bootstrap/Form";
import { getRVModelOrders } from "./GetRVModelOrders";
import { ModelFilter } from "./ModelFilter";
import { MyTableRenderer } from "./Table";
import { AssignOwnerModal } from "./AssignOwnerModal";
import { ReleaseOwnerModal } from "./ReleaseOwnerModal";
import { useHistory } from "react-router-dom";
import { AssignGroupModal, AssignGroupModalClosed } from "./AssignGroupModal";
import {
  AssignGroupEvent,
  OrderEvent,
  RowProps,
  LinkEvent,
  ReleaseEvent,
  SetHiddenEvent,
  canHideOrders,
} from "./Row";
import { RVModelOrderClient } from "../../../../api/api";
import { GetUserGroup, UserGroupChild } from "../../../../api/usergroup";
import { anyGranted, getUserGroupID } from "../../../../user/user";
import {
  LinkModal,
  UnlinkModal,
  LinkModalClosed,
  UnlinkModalClosed,
} from "./LinkModal";
import { ConnXClient, UserClient } from "../../../../api/apolloClient";
import { ApolloProvider } from "@apollo/react-hooks";
import {
  useSuccessFailureToasty,
  SuccessFailureToasty,
} from "../../../../components";

const RVModelOrderList = ({ ...rest }): JSX.Element => {
  // state
  const [displayIDs, setDisplayIDs] = useState(false);
  const [assignOrderID, setAssignOrderID] = useState<string | undefined>(
    undefined
  );
  const [releaseOrder, setReleaseOrder] = useState<ReleaseEvent | undefined>(
    undefined
  );
  const [linkOrder, setLinkOrder] = useState<{
    modelID?: string;
    orderID?: string;
  }>();
  const [unlinkOrder, setUnlinkOrder] = useState<{
    modelID?: string;
    orderID?: string;
    thingName?: string;
  }>();
  const {
    props: toastyProps,
    setSuccess: setToastySuccess,
    setFailure: setToastyFailure,
  } = useSuccessFailureToasty();
  const [listRefreshToken, setListRefreshToken] = useState(0);
  const [assignGroupOrder, setAssignGroupOrder] = useState<
    AssignGroupEvent | undefined
  >(undefined);

  // a model/order admin assigns orders within the group of the model being
  // listed, which they need not belong to; everyone else within their own group
  const admin = anyGranted("rv_model_admin");
  const [modelGroupID, setModelGroupID] = useState<string | undefined>();
  const getItems = useCallback(
    (opts: Parameters<typeof getRVModelOrders>[0]) => {
      setModelGroupID(opts.filterParams.model?.groupID);
      return getRVModelOrders(opts);
    },
    []
  );

  // the groups orders can be assigned to: that group and its child groups
  const [groups, setGroups] = useState<UserGroupChild[]>([]);
  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const groupID = admin ? modelGroupID : getUserGroupID();
        if (!groupID) {
          setGroups([]);
          return;
        }
        const g = (await GetUserGroup(groupID)).UserGroup;
        if (g && !cancelled) {
          setGroups([{ id: g.id, name: g.name }, ...(g.children || [])]);
        }
      } catch (e) {
        // not in a group, or it cannot be read: orders stay where they are
        console.log(e);
      }
    };
    load();
    return (): void => {
      cancelled = true;
    };
  }, [admin, modelGroupID]);
  const groupNames = useMemo(
    () =>
      groups.reduce<Record<string, string>>(
        (acc, g) => ({ ...acc, [g.id]: g.name }),
        {}
      ),
    [groups]
  );

  // handlers
  const handleCheckboxChanged = useCallback(() => {
    setDisplayIDs((d) => !d);
  }, []);
  const handleAssignOwner = (orderID: string): void => {
    setAssignOrderID(orderID);
  };

  const history = useHistory();

  // hide an order, or show it again; the action is reversible from the same
  // menu, so there is no confirmation
  const handleSetHidden = async (e: SetHiddenEvent): Promise<void> => {
    try {
      await RVModelOrderClient.setHidden(e.modelID, e.orderID, e.hidden);
      setToastySuccess(e.hidden ? "Hidden OK" : "Unhidden OK");
      setListRefreshToken((p) => p + 1);
    } catch (err) {
      console.error(err);
      setToastyFailure(e.hidden ? "Failed to hide" : "Failed to unhide");
    }
  };

  const rowProps: RowProps = {
    ...rest,
    displayIDs: displayIDs,
    groupNames: groupNames,
    onAssignGroup: setAssignGroupOrder,
    canAssignGroup: anyGranted("rv_order_update"),
    onSetHidden: handleSetHidden,
    canHide: canHideOrders(anyGranted),
    onAssignOwner: handleAssignOwner,
    onRelease: setReleaseOrder,
    onEdit: (e: OrderEvent): void => {
      history.push(`/connx/rvmodels/edit/order/${e.modelID}/${e.orderID}`);
    },
    onDelete: (e: OrderEvent): Promise<boolean> =>
      RVModelOrderClient.delete(e.modelID, e.orderID),
    onLink: (e: LinkEvent): void => {
      if (e.thingName && "" !== e.thingName) {
        setUnlinkOrder(e);
      } else {
        setLinkOrder(e);
      }
    },
    canDelete: anyGranted("rv_order_delete"),
    canUpdate: anyGranted("rv_order_update"),
    canAssignOrder: anyGranted("rv_owner_assign"),
    canRelease: anyGranted("rv_owner_revoke"),
    showOwner: anyGranted("rv_owner_list"),
    canLink: anyGranted("provisioning_link_thing"),
  };

  const handleLinkModalClose = (e: LinkModalClosed): void => {
    setLinkOrder(undefined);
    if (e.cancelled) return;
    if (e.err) {
      setToastyFailure("Linking failed");
    } else {
      setListRefreshToken((p) => p + 1);
      setToastySuccess("Linked OK");
    }
  };

  const handleAssignGroupClose = (e: AssignGroupModalClosed): void => {
    setAssignGroupOrder(undefined);
    if (e.cancelled) return;
    if (e.err) {
      setToastyFailure(e.err);
    } else {
      setToastySuccess("Assigned to group OK");
      setListRefreshToken((p) => p + 1);
    }
  };

  const handleUnlinkModalClose = (e: UnlinkModalClosed): void => {
    setUnlinkOrder(undefined);
    if (e.cancelled) return;
    if (e.err) {
      setToastyFailure("Unlinking failed");
    } else {
      setToastySuccess("Unlinked OK");
      setListRefreshToken((p) => p + 1);
    }
  };

  return (
    <>
      <SuccessFailureToasty
        successMessage="Updated OK"
        failureMessage="Failed to update"
        {...toastyProps}
      />
      <ApolloProvider client={UserClient}>
        <AssignOwnerModal
          orderID={assignOrderID || ""}
          show={undefined !== assignOrderID}
          onDone={(): void => setAssignOrderID(undefined)}
          onAssigned={(): void => setListRefreshToken((p) => p + 1)}
        />
        <ReleaseOwnerModal
          orderID={releaseOrder?.orderID || ""}
          orderName={releaseOrder?.name}
          show={undefined !== releaseOrder}
          onDone={(): void => setReleaseOrder(undefined)}
          onChanged={(): void => setListRefreshToken((p) => p + 1)}
        />
      </ApolloProvider>
      <AssignGroupModal
        open={undefined !== assignGroupOrder}
        groups={groups}
        onClose={handleAssignGroupClose}
        {...assignGroupOrder}
      />
      <ApolloProvider client={ConnXClient}>
        <LinkModal
          open={undefined !== linkOrder}
          onClose={handleLinkModalClose}
          {...linkOrder}
        />
        <UnlinkModal
          open={undefined !== unlinkOrder}
          onClose={handleUnlinkModalClose}
          thingName={unlinkOrder?.thingName || ""}
          {...unlinkOrder}
        />
      </ApolloProvider>
      <PaginatedSearchList
        getItems={getItems}
        ListRenderer={MyTableRenderer}
        CustomFilters={ModelFilter}
        refresh={listRefreshToken}
        {...rowProps}
      >
        {{
          tableOptions: (
            <Form.Check
              label="Display IDs"
              checked={displayIDs}
              onChange={handleCheckboxChanged}
            />
          ),
        }}
      </PaginatedSearchList>
    </>
  );
};

export default RVModelOrderList;
